/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseService, NotificationService, Roles, SaveAndQueryComponent, StatusService } from '@core';
import { AccountUnitOfWork } from '@data';
import {
  Account,
  AccountAdmin,
  AccountImportCodeOption,
  ActiveStatus,
  ActiveStatusEnum,
  Program,
  ProximityAreaEnum,
  ProximityRightEnum,
  ProximityRole,
  ProximityUser,
} from '@models';
import { DateFns, UtilFns } from '@utils';
import { Entity, EntityError } from 'breeze-client';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountActivationService } from './services/account-activation.service';
import { AccountDbQueryService } from './services/account-db-query.service';
import { AccountDbSaveService } from './services/account-db-save.service';

export class ImportOption {
  public constructor(init?: Partial<ImportOption>) {
    Object.assign(this, init);
  }
  id?: number;
  name?: string;
}

@Component({
  selector: 'prox-account-frm',
  templateUrl: './account-frm.component.html',
  standalone: false,
})
export class AccountFrmComponent extends SaveAndQueryComponent {
  accountId!: string;
  account!: Account;

  proximityUser!: ProximityUser;
  accountAdmin!: AccountAdmin;
  accountAdmins: AccountAdmin[] = [];
  filterAccountAdmins: AccountAdmin[] = [];
  areas = [ProximityAreaEnum.Accounts];
  allRoles: ProximityRole[] = [];
  activeStatuses: ActiveStatus[] = [];
  accountImportCodeOptions: AccountImportCodeOption[] = [];
  importOptions: ImportOption[] = [];
  deleteLabel = 'Delete this Account';

  isBeingAdded!: boolean;
  addedEntities: Entity[] = [];

  fiscalMonthNum = 1;
  fiscalMonthNums!: any[];
  fiscalDayNum = 1;
  fiscalDayNums!: any[];
  currentFiscalPeriod = '';

  isValidateActivated = false;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private uow: AccountUnitOfWork,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
    public pngDialogService: DialogService,
    private statusService: StatusService,
    public activationService: AccountActivationService,
    private notificationService: NotificationService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object) {
    const accountId = params['accountId'];
    UtilFns.assertNonEmptyString(accountId, 'accountId');
    // The field was declared but never assigned, so anything reading this.accountId got undefined.
    this.accountId = accountId;

    [this.activeStatuses, this.accountImportCodeOptions] = await Promise.all([
      this.dbQueryService.getAll(ActiveStatus),
      this.dbQueryService.getAll(AccountImportCodeOption),
    ]);

    this.isBeingAdded = accountId === 'add';

    if (this.isBeingAdded) {
      this.allRoles = (await this.dbQueryService.getAllRoles()).filter((p) => p.name === Roles.PrincipalAccountAdmin);
    } else {
      this.allRoles = await this.dbQueryService.getAccountRolesByAccountId(accountId);
    }

    if (this.isBeingAdded) {
      this.createEntities();
    } else {
      this.account = await this.dbQueryService.getAccountByIdWithAdmins(accountId);
      this.accountAdmins = this.account.accountAdmins;
      // proximityRole?.name, not .name: an administrator with no Role would otherwise throw here,
      // and this runs on page LOAD — one bad row would break the whole Account form rather than
      // just one save. Same defect as account-administrator-frm.ts:240. undefined === the role
      // name is false, so such an administrator simply is not counted as principal, which is
      // correct: no role means no principal privileges.
      this.filterAccountAdmins = this.account.accountAdmins.filter(
        (p) =>
          p.proximityUser.activeStatusId === ActiveStatusEnum.Active &&
          p.proximityUser.proximityRole?.name === Roles.PrincipalAccountAdmin,
      );
    }
    UtilFns.assertNonNull(this.account, 'account');

    this.isValidateActivated = this.activationService.hasErrors;

    this.accountImportCodeOptions.forEach((p) => {
      this.importOptions.push(
        new ImportOption({
          id: p.id,
          name: p.description,
        }),
      );
    });

    this.fiscalMonthNums = [...Array(12).keys()].map((x) => {
      return { label: (x + 1).toString(), value: x + 1 };
    });

    this.fiscalDayNums = [...Array(31).keys()].map((x) => {
      return { label: (x + 1).toString(), value: x + 1 };
    });

    // if fiscalMonthStartDate was never set - set it now.
    if (this.account.fiscalMonthDayStartDate == null) {
      // year doesn't matter.
      this.account.fiscalMonthDayStartDate = DateFns.startOfMonth(new Date(2024, 1, 1));
      await this.dbSaveService.saveSelectedChanges([this.account]);
    }

    this.fmtCurrentFiscalPeriod();
    this.fiscalDayNum = this.account.fiscalMonthDayStartDate.getDate();
    this.fiscalMonthNum = this.account.fiscalMonthDayStartDate.getMonth() + 1;

    this.setTitle('Account Information');
    this.isPageReady = true;
  }

  override get entity() {
    return this.account;
  }

  /**
   * The eyeball on this form means "everything that happened on this account", not "the edits
   * made to the Accounts row". The generic version in save-component-mixin passes `entity`, which
   * filters ChangeLog down to rows whose EntityId is this account's own guid - about a dozen
   * status flips. Passing accountId instead reads the whole account's history.
   */
  override showChangeLog() {
    // this.account.id, not this.accountId - the latter is never assigned, and an empty value here
    // is what let this dialog show every account's history at once.
    return this.dialogService.changeLogDialog(this.dbSaveService.uow, null, undefined, undefined, this.account.id);
  }

  /*   errorsString() {
    this.activationService.deactivate();
  } */

  fiscalDateChanged() {
    const origFiscalDayNum = this.account.fiscalMonthDayStartDate.getDate();
    const origFiscalMonthNum = this.account.fiscalMonthDayStartDate.getMonth() + 1;
    const checkDate = new Date(2025, this.fiscalMonthNum - 1, this.fiscalDayNum);
    const isValid =
      Boolean(+checkDate) &&
      checkDate.getMonth() + 1 === this.fiscalMonthNum && // Add 1 back to match input month (1-12)
      checkDate.getDate() === this.fiscalDayNum;

    if (!isValid) {
      this.dialogService.okDialog('Invalid Date', 'The selected Fiscal Month/Day combination is not a valid date.');
      return;
    }

    if (this.fiscalDayNum == origFiscalDayNum && this.fiscalMonthNum == origFiscalMonthNum) return;
    this.account.fiscalMonthDayStartDate = checkDate;
    this.fmtCurrentFiscalPeriod();
  }

  fmtCurrentFiscalPeriod() {
    const dt = this.account.getCurrentYearFiscalStartDate();
    this.currentFiscalPeriod = DateFns.fmtDate(dt) + ' to ' + DateFns.fmtDate(DateFns.dateAdd(DateFns.dateAdd(dt, 1, 'year'), -1, 'day'));
  }

  private createEntities() {
    this.account = this.dbSaveService.createEntity(Account, {
      activeStatusId: ActiveStatusEnum.Hold,
      startDate: new Date(),
      fiscalMonthDayStartDate: new Date(),
    });

    const role = this.allRoles.find((r) => r.name == Roles.PrincipalAccountAdmin);
    this.proximityUser = this.dbSaveService.createEntity(ProximityUser, {
      activeStatusId: ActiveStatusEnum.Active,
      proximityRoleId: role?.id,
    });

    this.accountAdmin = this.dbSaveService.createEntity(AccountAdmin, {
      accountId: this.account.id,
      proximityUserId: this.proximityUser.id,
    });

    // this.allRoles.forEach((p) => {
    //   if (p.areaId == 1) {
    //     const applied = this.dbSaveService.createEntity(ProximityUserRight, {
    //       proximityUserId: this.proximityUser.id,
    //       proximityRightId: p.id,
    //     });
    //     this.appliedUserRights.push(applied);
    //   }
    // });

    this.addedEntities = [this.account, this.accountAdmin, this.proximityUser];
    this.addedEntities.forEach((e) => {
      e.entityAspect.setUnchanged();
    });
  }

  override async addCrossValidationErrors(): Promise<void> {
    if (!this.isBeingAdded) {
      if (this.account.primaryAccountAdminId == null) {
        this.createValidationError(this.account, null, 'Active Proximity Accounts must have a Primary Administrator');
      } else {
        if (this.account.primaryAccountAdmin.getStatusId() != ActiveStatusEnum.Active) {
          this.createValidationError(this.account, null, 'Account Primary Admninistrator must be status: Active');
        }
        if (this.account.primaryAccountAdmin.getRoleName() != Roles.PrincipalAccountAdmin) {
          this.createValidationError(this.account, null, 'Account Principal Administrator must have PrincipalAdministrator privileges');
        }
      }
    } else {
      if (this.account.primaryAccountAdminId != null) {
        if (this.account.primaryAccountAdmin.getStatusId() != ActiveStatusEnum.Active) {
          this.createValidationError(this.account, null, 'Account Primary Admninistrator must be status: Active');
        }
        if (this.account.primaryAccountAdmin.getRoleName() != Roles.PrincipalAccountAdmin) {
          this.createValidationError(this.account, null, 'Account Principal Administrator must have PrincipalAdministrator privileges');
        }
      }
    }
  }

  statusButtonLabel() {
    if (this.isValidateActivated) {
      return 'Cancel Activation';
    }

    if (this.account.activeStatusId == ActiveStatusEnum.Active) {
      return 'Place Account on Hold';
    } else if (this.account.activeStatusId == ActiveStatusEnum.Hold) {
      return 'Activate Account';
    }
    return 'Contact Proximity Support';
  }

  disableViewAdministrator() {
    return this.dbSaveService.hasChanges() || !this.account.primaryAccountAdmin || !this.canEdit();
  }

  gotoAdministrator() {
    this.router.navigate(['account', 'accounts', this.account.id, 'manage', 'account-administrators', this.account.primaryAccountAdmin.id]);
  }

  override navigateToValidationError(error: EntityError) {
    const errEnt = error.entity;
    const prop = error.propertyName;
    UtilFns.focusInputByEntity('#topLevel', errEnt, prop);
  }

  override navigateBack(): void {
    this.router.navigate(['account/accounts']);
  }

  statusMessage(): string {
    if (this.account.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account.activeStatusId == ActiveStatusEnum.Hold) {
      return 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account.activeStatus.name + ' and is Not Editable';
    }
  }

  canEdit(): boolean {
    const authUser = this.baseService.authService.getUser();
    return authUser != null && authUser.hasRight(ProximityRightEnum.CanManageAccountAdministration);
  }

  isReadOnly(): boolean {
    return !this.canEdit() || this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
  }

  okToDelete() {
    return !this.isBeingAdded && this.account.activeStatusId == ActiveStatusEnum.Hold;
  }

  async onDelete() {
    const inUse = await this.dbQueryService.checkIfInUse(this.account, Program, 'accountId');
    if (inUse) {
      this.toastr.warning(`You cannot delete an Account that has applied Programs.`, 'Cannot Delete');
      return;
    }

    const ok = await this.dialogService.askIfOk(
      'Delete Account',
      'Are you sure that you want to delete ' + this.account?.name + '?  (This action cannot be reversed)',
    );
    if (!ok) {
      return;
    }

    // insure that everything we want to delete is in the cache.
    await this.dbQueryService.getAccountByIdForDelete(this.account.id);

    // circular ref here - clear it first.
    this.account.primaryAccountAdminId = undefined;
    await this.saveChanges();

    this.account.accountImages.slice().forEach((c) => {
      c.entityAspect.setDeleted();
    });

    this.account.accountAdmins.slice().forEach((c) => {
      c.proximityUser.entityAspect.setDeleted();
      c.entityAspect.setDeleted();
    });

    this.account.accountAdminGroups.slice().forEach((c) => {
      c.accountAdminGroupMaps.slice().forEach((d) => {
        d.entityAspect.setDeleted();
      });
      c.entityAspect.setDeleted();
    });

    this.account.shippingUserGroups.slice().forEach((c) => {
      c.shippingUserGroupMaps.slice().forEach((d) => {
        d.entityAspect.setDeleted();
      });
      c.shippingUserGroupAddressMaps.slice().forEach((e) => {
        e.entityAspect.setDeleted();
      });
      c.entityAspect.setDeleted();
    });

    this.account.programUserGroups.slice().forEach((c) => {
      c.programUserGroupBudgets.slice().forEach((d) => {
        d.entityAspect.setDeleted();
      });
      c.programUserGroupMaps.slice().forEach((f) => {
        f.entityAspect.setDeleted();
      });
      c.programUserGroupExclusions.slice().forEach((g) => {
        g.entityAspect.setDeleted();
      });
      c.entityAspect.setDeleted();
    });

    this.account.accountUsers.slice().forEach((c) => {
      c.proximityUser.entityAspect.setDeleted();
      c.entityAspect.setDeleted();
    });

    this.account.budgetProductTypeTags.slice().forEach((c) => {
      c.budgetProductTypeTagMaps.slice().forEach((d) => {
        d.entityAspect.setDeleted();
      });
      c.entityAspect.setDeleted();
    });

    this.account.programProductTypeTags.slice().forEach((c) => {
      c.programProductCategoryTagMaps.slice().forEach((d) => {
        d.entityAspect.setDeleted();
      });
      c.programProductTypeTagMaps.slice().forEach((e) => {
        e.entityAspect.setDeleted();
      });
      c.entityAspect.setDeleted();
    });

    this.account.accountAddresses.slice().forEach((c) => {
      c.entityAspect.setDeleted();
    });

    this.account.entityAspect.setDeleted();

    const r = await this.saveChanges();
    this.toastr.success('Account was successfully deleted', 'Database Activity');
    this.navigateBack();
  }

  async toggleActivate() {
    if (this.isBeingAdded) {
      return;
    }

    if (this.isValidateActivated) {
      const ok = await this.dialogService.askIfOk('Cancel Activation', 'This will discard any unsaved changes and exit Activation Mode. Do you want to proceed?');
      if (ok) {
        this.dbSaveService.rejectChanges();
        this.activationService.deactivate();
        this.isValidateActivated = false;
      }
      return;
    }

    if (this.account.activeStatusId != ActiveStatusEnum.Active && this.account.activeStatusId != ActiveStatusEnum.Hold) {
      return;
    }

    if (this.account.activeStatusId === ActiveStatusEnum.Hold) {
      if (await this.activationService.activate(this.account.id)) {
        const ok = await this.dialogService.askIfOk(
          'Cannot Activate',
          'Account cannot be activated.  Switch to Account Activation Mode for fixes?',
        );
        if (ok) {
          this.isValidateActivated = true;
          return;
        } else {
          this.isValidateActivated = false;
          this.activationService.deactivate();
          return;
        }
      }
    }

    const pendingActiveStatusId = this.account.activeStatusId == ActiveStatusEnum.Active ? ActiveStatusEnum.Hold : ActiveStatusEnum.Active;

    const ok = await this.dialogService.verifyProceedDialog(
      'Account Status Change',
      this.statusService.getStatusExplanation(pendingActiveStatusId, 'Account'),
    );
    if (ok) {
      this.account.activeStatusId = pendingActiveStatusId;
      await this.saveChanges();
      this.toastr.success('Status change updated', 'Database Activity');

      if (pendingActiveStatusId === ActiveStatusEnum.Hold) {
        const accountLink = `/account/accounts/${this.account.id}/manage`;
        this.notificationService.add(
          'warn',
          'Account On Hold',
          `<a href="${accountLink}">${this.account.name}</a> is on Hold. Remember to re-activate when editing is complete.`,
          undefined,
          `account-hold:${this.account.id}`,
        );
      } else {
        this.notificationService.resolveByKey(`account-hold:${this.account.id}`);
      }
    }
  }

  override async beforeSave() {
    const nameOk = await this.dbQueryService.checkIfIsUnique(this.account, 'id', 'name');
    if (!nameOk) {
      await this.dialogService.warnDialog('Unable to Save Account', 'An account with this name already exists. Please choose a different name.');
      return false;
    }
    if (this.isBeingAdded) {
      this.addedEntities.forEach((e) => e.entityAspect.setAdded());
      this.proximityUser.entityAspect.validateEntity();
      const adminErrors = this.proximityUser.entityAspect.getValidationErrors();
      if (adminErrors.length > 0) {
        const msg = adminErrors.map((e) => e.errorMessage).join('\n');
        await this.dialogService.warnDialog('Unable to Add Account', 'Principal Administrator details are incomplete:\n' + msg);
        this.addedEntities.forEach((e) => e.entityAspect.setUnchanged());
        return false;
      }
      // Authoritative check via Identity, not Breeze: catches orphan AspNetUsers rows
      // (see Amanda Bortz 2026-05-25 repro).
      const ok = await this.baseService.authService.isUsernameAvailable(this.proximityUser.username);
      if (!ok) {
        await this.dialogService.warnDialog('Unable to Add Account', 'This Administrator Login Name has already been taken.');
        this.addedEntities.forEach((e) => e.entityAspect.setUnchanged());
        return false;
      }
      if (this.proximityUser.email) {
        const emailOk = await this.baseService.authService.isEmailAvailable(this.proximityUser.email, this.proximityUser.id);
        if (!emailOk) {
          await this.dialogService.warnDialog('Unable to Add Account', 'This Administrator email address is already in use by another user.');
          this.addedEntities.forEach((e) => e.entityAspect.setUnchanged());
          return false;
        }
      }
    }
    return true;
  }

  override async afterSave() {
    if (this.isBeingAdded) {
      // because of circular dependency issues.
      this.account.primaryAccountAdminId = this.accountAdmin.id;
      await this.saveChanges();
      this.isBeingAdded = false;
      this.router.navigate(['account/accounts', this.account.id, 'manage']);
    }

    if (this.isValidateActivated) {
      if (!(await this.activationService.activate(this.account.id))) {
        this.account.activeStatusId = ActiveStatusEnum.Active;
        this.activationService.deactivate();
        this.isValidateActivated = false;
        await this.saveChanges();
        this.notificationService.resolveByKey(`account-hold:${this.account.id}`);
      }
    }
  }

  override async afterUndo() {
    if (this.isBeingAdded) {
      this.createEntities();
    }
  }
}
